// src/billing/useChangeNow.js — the «Passa ora» flow (v204): preview → confirm with the quote → re-quote once.
//
// ⛔ An immediate change charges the saved card with NO Stripe page, so it is never sent without a preview the user
// has seen: `preview(option)` asks POST /change-plan/preview for the amount, the card and a 5-minute `quote`, and
// returns the page's pending confirmation (`{ kind:'change', option, preview, codes, discloses }`, worded by
// billing/confirmContent.js). A usable card with an amount we cannot print throws PREVIEW_UNUSABLE rather than
// offering a charge we cannot show — and so does one without a quote (the total could not be locked; `submit` also
// refuses such a confirmation, sending nothing). `submit(pending)` sends the change WITH the quote; when the quote
// expired or the total moved (409 QUOTE_EXPIRED / 422 QUOTE_INVALID) it previews again and returns `{ requote }` —
// the same confirmation, marked `quoteChanged` and counting `requotes`, for the user to confirm the new figure. Up to
// MAX_REQUOTES in a row: the credit for unused time shrinks by the second, so the total moves ~1 cent every few
// minutes (measured on the dev VPS, 2026-10-10) and a prompt click can still land just after a step. The next quote
// failure propagates (a terminal outcome, never an endless re-confirm loop). Anything else propagates.
// ⭐ Sequence ticket: a preview — answer OR failure — overtaken by a newer one or by a workspace switch resolves null.
import { useCallback, useEffect, useRef } from 'react';
import { changePlan, previewChangePlan } from './billingApi.js';
import { chargeRows } from './money.js';

const REQUOTE_CODES = new Set(['QUOTE_EXPIRED', 'QUOTE_INVALID']);
/** Re-quotes in a row before a quote failure becomes the outcome. */
export const MAX_REQUOTES = 3;

const unusable = () => Object.assign(new Error('preview without a printable amount'), { code: 'PREVIEW_UNUSABLE' });

/** The page's pending confirmation for a preview. */
function toPending(option, preview) {
  const card = preview?.card;
  const quoted = typeof preview?.quote === 'string' && preview.quote !== '';
  if (card && card.expired !== true && (!chargeRows(preview.amount) || !quoted)) throw unusable();
  return {
    kind: 'change', option, preview,
    codes: preview?.disclosure_codes || [], discloses: preview?.discloses || [],
  };
}

export default function useChangeNow(tenantId) {
  const seq = useRef(0);

  // A workspace switch invalidates any preview in flight.
  useEffect(() => { seq.current += 1; }, [tenantId]);

  const preview = useCallback(async (option) => {
    seq.current += 1;
    const ticket = seq.current;
    let answer;
    try {
      answer = await previewChangePlan(option.plan_id);
    } catch (err) {
      if (ticket !== seq.current) return null;
      throw err;
    }
    if (ticket !== seq.current) return null;
    return toPending(option, answer);
  }, []);

  const submit = useCallback(async (pending) => {
    const quote = pending.preview?.quote;
    if (pending.preview && !quote) throw unusable();   // ⛔ an immediate change never goes out unquoted
    try {
      return { response: await changePlan(pending.option.plan_id, quote ? { quote } : {}) };
    } catch (err) {
      const requotes = pending.requotes || 0;
      if (!quote || requotes >= MAX_REQUOTES || !REQUOTE_CODES.has(err?.code)) throw err;
      const next = await preview(pending.option);
      return { requote: next && { ...next, quoteChanged: true, requotes: requotes + 1 } };
    }
  }, [preview]);

  return { preview, submit };
}
